import React, { useMemo } from "react";
import {
	Avatar,
	Box,
	Card,
	CardContent,
	CardHeader,
	Chip,
	Divider,
	Grid2,
	Paper,
	Stack,
	Typography
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { AnonymizedTalent } from "../../models/AnonymizedTalent";
import { zoullooColors } from "../../theme";
import { sanitizeHtml } from "../../utils/sanitizeHtml";
import StarRating from "../project/evaluation/StarRating";

/**
 * Detail view of an anonymized talent rendered with the SAME visual language
 * as the rest of the platform (mirrors <c>FreelancerDetails</c>):
 *   - MUI Card + CardHeader + CardContent + Divider for every section
 *   - Outlined Chips for tags / skills / sectors / domains
 *   - <c>freelancerDetails.*</c> i18n keys reused so titles match what clients
 *     already see elsewhere on the platform
 *
 * This is the in-app preview used by connected clients in "Mes talents
 * suggérés". The V5.1 "bold editorial" fiche is reserved for the OUT-OF-APP
 * public page reached by anonymous prospects via a token URL.
 *
 * Anonymization rules come from the API DTO: presentation already truncated,
 * skills already filtered to VeryGood, tools to Mastered, names reduced to
 * initials, etc. The component is presentation-only.
 */
interface Props {
	talent: AnonymizedTalent;
}

const SECTION_CARD_SX = {
	margin: "16px 0",
	borderRadius: "8px"
} as const;

export const AnonymizedFreelancerDetailsView: React.FC<Props> = ({ talent }) => {
	const { t } = useTranslation();

	const expertiseLabel = talent.expertise?.label
		? t(`Expertises.${talent.expertise.label}`, talent.expertise.label)
		: "";
	const experienceLabel = talent.experience?.label
		? t(`Experiences.${talent.experience.label}`, talent.experience.label)
		: "";
	const yearsLabel = talent.freelancingYearsOfExperience?.label
		? t(
				`FreelancingYearsOfExperiences.${talent.freelancingYearsOfExperience.label}`,
				talent.freelancingYearsOfExperience.label
			)
		: "";

	const sectorsLabels = talent.sectors.map((s) =>
		t(`Sectors.${s.sector.label}`, s.sector.label)
	);
	const domainsLabels = talent.businessDomains.map((d) =>
		t(`BusinessDomains.${d.businessDomain.label}`, d.businessDomain.label)
	);
	const skillsLabels = talent.topSkills.map((s) =>
		t(`Skills.${s.label}`, s.label)
	);
	const toolsLabels = talent.masteredTools.map((tool) =>
		t(`Tools.${tool.label}`, tool.label)
	);

	const presentationHtml = useMemo(
		() => sanitizeHtml(talent.presentation),
		[talent.presentation]
	);

	return (
		<Box sx={{ p: { xs: 2, md: 3 } }}>
			<Header
				talent={talent}
				expertiseLabel={expertiseLabel}
				experienceLabel={experienceLabel}
				yearsLabel={yearsLabel}
			/>

			{/* Title + Presentation — the card header IS the profile title now
			    (falls back to the generic label when the freelance left it blank). */}
			<Card component={Paper} elevation={0} sx={SECTION_CARD_SX}>
				<CardHeader
					title={
						<Typography variant="h6">
							{talent.title || t("freelancerDetails.profileTitle")}
						</Typography>
					}
				/>
				<Divider />
				<CardContent sx={{ p: 2 }}>
					{presentationHtml ? (
						<Box
							sx={{
								fontSize: "0.95rem",
								lineHeight: 1.6,
								color: zoullooColors.text.primary,
								"& p": { my: 0.75 },
								"& strong, & b": {
									fontWeight: 700,
									color: zoullooColors.secondary.dark
								},
								"& ul, & ol": { pl: 2.5, my: 0.5 }
							}}
							dangerouslySetInnerHTML={{ __html: presentationHtml }}
						/>
					) : (
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ fontStyle: "italic" }}
						>
							{t("freelancerDetails.notProvided")}
						</Typography>
					)}
					{talent.presentationTruncated && (
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ display: "block", mt: 1.5, fontStyle: "italic" }}
						>
							{t("anonymizedTalent.presentationTruncated")}
						</Typography>
					)}
				</CardContent>
			</Card>

			{/* Sectors & Domains */}
			<SectorsAndDomainsSection sectors={sectorsLabels} domains={domainsLabels} />

			{/* Skills */}
			<ChipSection
				title={t("freelancerDetails.sections.skills")}
				items={skillsLabels}
			/>

			{/* Tools */}
			<ChipSection
				title={t("freelancerDetails.sections.tools")}
				items={toolsLabels}
			/>

			{/* Experiences */}
			<ExperiencesSection talent={talent} />
		</Box>
	);
};

// ============================================================
// Header — Avatar with initials + identity strip
// ============================================================
const Header: React.FC<{
	talent: AnonymizedTalent;
	expertiseLabel: string;
	experienceLabel: string;
	yearsLabel: string;
}> = ({ talent, expertiseLabel, experienceLabel, yearsLabel }) => {
	const { t } = useTranslation();
	const initials =
		`${talent.firstNameInitial ?? ""}.${talent.lastNameInitial ?? ""}.`.toUpperCase();
	const avgRating = Number(talent.evaluations?.averageRating ?? 0);
	const totalRatings = talent.evaluations?.totalCount ?? 0;

	return (
		<Box sx={{ p: 2, position: "relative" }}>
			<Stack direction={{ xs: "column", sm: "row" }} spacing={3} alignItems="center">
				<Avatar
					sx={{
						width: 100,
						height: 100,
						fontSize: "1.75rem",
						fontWeight: 700,
						backgroundColor: zoullooColors.proposal.magnolia,
						color: zoullooColors.secondary.dark
					}}
				>
					{initials}
				</Avatar>

				<Stack spacing={0} flex={1}>
					<Box
						display="flex"
						alignItems="center"
						gap={2}
						flexWrap="wrap"
					>
						<Typography variant="h1">{initials}</Typography>
						{totalRatings > 0 && (
							<Box display="flex" alignItems="center" gap={0.5}>
								<StarRating value={avgRating} readOnly size="small" />
								<Typography variant="body2" color="text.secondary">
									({totalRatings})
								</Typography>
							</Box>
						)}
					</Box>
					<Box ml={0.5}>
						<Typography variant="body1">
							{expertiseLabel || (
								<Typography variant="body2" fontStyle="italic" component="span">
									{t("freelancerDetails.notProvided")}
								</Typography>
							)}
							{experienceLabel ? ` — ${experienceLabel}` : ""}
						</Typography>
						<Typography variant="body1" color="text.secondary">
							{t("freelancerDetails.freelancerSince")}{" "}
							{yearsLabel || (
								<Typography variant="body2" fontStyle="italic" component="span">
									({t("freelancerDetails.notProvided")})
								</Typography>
							)}
						</Typography>
					</Box>
				</Stack>
			</Stack>

			{/* Anonymized badge — clearly tells the client these are anonymized profiles. */}
			<Chip
				label={t("anonymizedTalent.anonymousPill")}
				size="small"
				sx={{
					position: { xs: "static", sm: "absolute" },
					top: 8,
					right: 8,
					mt: { xs: 2, sm: 0 },
					backgroundColor: zoullooColors.proposal.seashell,
					color: zoullooColors.primary.dark,
					fontWeight: 700,
					letterSpacing: "0.4px",
					textTransform: "uppercase",
					fontSize: 10
				}}
			/>
		</Box>
	);
};

// ============================================================
// Generic chip section (skills / tools / etc.)
// ============================================================
const ChipSection: React.FC<{
	title: string;
	items: string[];
	levelLabel?: string;
}> = ({ title, items, levelLabel }) => {
	const { t } = useTranslation();

	return (
		<Card component={Paper} elevation={0} sx={SECTION_CARD_SX}>
			<CardHeader title={<Typography variant="h6">{title}</Typography>} />
			<Divider />
			<CardContent sx={{ p: 1.5 }}>
				{items.length === 0 ? (
					<Typography
						variant="body1"
						color="text.secondary"
						sx={{ fontStyle: "italic", textAlign: "center" }}
					>
						{t("freelancerDetails.notProvided")}
					</Typography>
				) : (
					<Box display="flex" flexWrap="wrap" gap={0.5}>
						{items.map((item) => (
							<Chip
								key={item}
								label={
									<Box display="inline-flex" alignItems="center" gap={0.75}>
										<Typography variant="body2">{item}</Typography>
										{levelLabel && (
											<Typography
												variant="caption"
												sx={{
													color: zoullooColors.primary.main,
													fontWeight: 700,
													textTransform: "uppercase",
													letterSpacing: "0.4px"
												}}
											>
												{levelLabel}
											</Typography>
										)}
									</Box>
								}
								size="medium"
								variant="outlined"
							/>
						))}
					</Box>
				)}
			</CardContent>
		</Card>
	);
};

// ============================================================
// Sectors & Domains (two-column card, same layout as FreelancerDetails)
// ============================================================
const SectorsAndDomainsSection: React.FC<{
	sectors: string[];
	domains: string[];
}> = ({ sectors, domains }) => {
	const { t } = useTranslation();

	return (
		<Card component={Paper} elevation={0} sx={SECTION_CARD_SX}>
			<CardHeader
				title={
					<Typography variant="h6">
						{t("freelancerDetails.sections.sectorsAndDomains")}
					</Typography>
				}
			/>
			<Divider />
			<CardContent sx={{ p: 1.5 }}>
				{sectors.length === 0 && domains.length === 0 ? (
					<Typography
						variant="body1"
						color="text.secondary"
						sx={{ fontStyle: "italic", textAlign: "center" }}
					>
						{t("freelancerDetails.notProvided")}
					</Typography>
				) : (
					<Grid2 container spacing={2}>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<DomainColumn
								title={t("freelancerDetails.sections.sectors")}
								items={sectors}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 6 }}>
							<DomainColumn
								title={t("freelancerDetails.sections.domains")}
								items={domains}
							/>
						</Grid2>
					</Grid2>
				)}
			</CardContent>
		</Card>
	);
};

const DomainColumn: React.FC<{ title: string; items: string[] }> = ({
	title,
	items
}) => {
	const { t } = useTranslation();
	return (
		<>
			<Typography
				variant="body1"
				color="text.secondary"
				mb={2}
				display="block"
				fontSize="0.95rem"
			>
				{title}
			</Typography>
			{items.length === 0 ? (
				<Typography
					variant="body1"
					color="text.secondary"
					sx={{ fontStyle: "italic", textAlign: "center" }}
				>
					{t("freelancerDetails.notProvided")}
				</Typography>
			) : (
				<Box display="flex" flexWrap="wrap" gap={0.5}>
					{items.map((item) => (
						<Chip
							key={item}
							label={<Typography variant="body2">{item}</Typography>}
							size="medium"
							variant="outlined"
						/>
					))}
				</Box>
			)}
		</>
	);
};

// ============================================================
// Professional experiences (anonymized — company / role / type / stack)
// ============================================================
const ExperiencesSection: React.FC<{ talent: AnonymizedTalent }> = ({ talent }) => {
	const { t } = useTranslation();

	// Hide the whole block when there are no experiences (often the case for
	// now) rather than showing a "non renseigné" placeholder.
	if (talent.professionalExperiences.length === 0) {
		return null;
	}

	return (
		<Card component={Paper} elevation={0} sx={SECTION_CARD_SX}>
			<CardHeader
				title={
					<Typography variant="h6">
						{t("freelancerDetails.sections.experiences")}
					</Typography>
				}
			/>
			<Divider />
			<CardContent sx={{ p: 2 }}>
				{(
					<Stack spacing={2}>
						{talent.professionalExperiences.map((xp) => (
							<Box
								key={xp.id}
								sx={{
									p: 2,
									borderRadius: 1.5,
									border: `1px solid ${zoullooColors.grey[200]}`,
									backgroundColor: zoullooColors.background.lighter
								}}
							>
								<Box
									display="flex"
									justifyContent="space-between"
									alignItems="flex-start"
									flexWrap="wrap"
									gap={1}
								>
									<Box sx={{ minWidth: 0 }}>
										<Typography
											variant="subtitle1"
											sx={{
												fontWeight: 700,
												color: zoullooColors.secondary.dark
											}}
										>
											{xp.jobTitle ?? t("anonymizedTalent.missionDefaultRole")}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											{xp.companyName ?? t("anonymizedTalent.missionDefaultCompany")}
										</Typography>
									</Box>
									{xp.jobType?.label && (
										<Chip
											size="small"
											label={t(`JobTypes.${xp.jobType.label}`, xp.jobType.label)}
											variant="outlined"
											sx={{ fontWeight: 600 }}
										/>
									)}
								</Box>
								{xp.technicalStack && <TechnicalStackBlock raw={xp.technicalStack} />}
							</Box>
						))}
					</Stack>
				)}
			</CardContent>
		</Card>
	);
};

// ============================================================
// Technical stack — dual rendering
// ============================================================
//
// `technicalStack` is meant to be a comma-separated list of technologies, but
// freelancers fill it via a rich-text editor (Tiptap), so the value is
// regularly wrapped in HTML (`<p>...</p>`, `<br/>`, list items, etc.). We:
//   1. Sanitize the raw value (whitelist of safe inline tags only)
//   2. If anything beyond plain text survived sanitisation → render as HTML so
//      paragraphs / line breaks / bold are honored (same look as Presentation)
//   3. Otherwise → fall back to the comma-split chip layout that reads well
//      when the field really is "React, Node.js, TypeScript"
const TechnicalStackBlock: React.FC<{ raw: string }> = ({ raw }) => {
	const html = useMemo(() => sanitizeHtml(raw), [raw]);
	const hasMarkup = useMemo(() => /<[a-z][\s\S]*?>/i.test(html), [html]);

	if (hasMarkup) {
		return (
			<Box
				mt={1.5}
				sx={{
					fontSize: "0.9rem",
					lineHeight: 1.55,
					color: zoullooColors.text.primary,
					"& p": { my: 0.5 },
					"& strong, & b": {
						fontWeight: 700,
						color: zoullooColors.secondary.dark
					},
					"& ul, & ol": { pl: 2.5, my: 0.5 }
				}}
				dangerouslySetInnerHTML={{ __html: html }}
			/>
		);
	}

	return (
		<Box mt={1.5} display="flex" flexWrap="wrap" gap={0.5}>
			{raw
				.split(/[,;]/)
				.map((tech) => tech.trim())
				.filter(Boolean)
				.map((tech, index) => (
					// Suffix the index so duplicate techs don't trigger React's
					// duplicate-key warning.
					<Chip
						key={`${tech}-${index}`}
						label={<Typography variant="caption">{tech}</Typography>}
						size="small"
						variant="outlined"
					/>
				))}
		</Box>
	);
};

export default AnonymizedFreelancerDetailsView;
